import type { Ref } from 'vue'
import pLimit from 'p-limit'
import { isTooLarge, MAX_UPLOAD_LABEL } from '~~/shared/allowedFileTypes'
import type { UploadedItem } from '~~/shared/items/uploadedItem'
import { apiErrorMessage } from '~/utils/apiErrorMessage'
import type { useApi } from '~/composables/useApi'
import type { IndexJob, IndexSource, UploadFailureKind } from '~/composables/documents-indexing/indexJob'
import { requestFailureKind } from '~/utils/requestFailure'
import { sendUpload, type OneDriveRef } from '~/composables/documents-indexing/uploadTransport'

/**
 * Simultaneous upload requests.
 *
 * The endpoint's own `pLimit(8)` bounded files within ONE request; splitting per file moved that
 * responsibility to the client, and a ten-file drop must not open ten connections. Below the
 * server's number on purpose — this cap is per tab, and a user may have several open.
 */
const UPLOAD_CONCURRENCY = 4

export interface IndexingUploadsDeps {
  apiFetch: ReturnType<typeof useApi>['apiFetch']
  t: (key: string, params?: Record<string, unknown>) => string
  jobs: Ref<IndexJob[]>
  /** The job lifecycle stays the store's: these are its own functions, handed in. */
  mintJob: (input: { name: string; path: string; source: IndexSource }) => IndexJob
  pushJob: (job: IndexJob) => IndexJob
  claim: (job: IndexJob, item: UploadedItem) => void
  /** `kind` beside the message: the message is the Activity Center's, the kind is for surfaces that translate. */
  fail: (job: IndexJob, message: string, kind: UploadFailureKind) => void
  trim: () => void
  ensurePoll: () => void
  /** Open the Activity Center — on the DROP, not on the response. */
  reveal: () => void
}

/**
 * Uploads as indexing jobs: the drop fan-out, the per-file abort handles and the failure wording
 * (MAR-948 — split out of the indexing store, whose `upload` and `cancel` are these). The wire is
 * `sendUpload`; the job state machine stays in the store and is handed in.
 */
export function useIndexingUploads(deps: IndexingUploadsDeps) {
  const { apiFetch, t, jobs, mintJob, pushJob, claim, fail, trim, ensurePoll, reveal } = deps

  /**
   * Live upload requests, by job id — what makes an upload cancellable at all.
   *
   * Deliberately NOT on the job: an `AbortController` is not state the panel renders, and putting it
   * on a reactive object would have Vue proxy it for no reason.
   */
  const aborters = new Map<string, AbortController>()

  /**
   * THE upload entry point — every surface in the app routes through here (MAR-113).
   *
   * Two decisions carry this function.
   *
   * **Rows are minted BEFORE the request.** Registering after it resolved meant nothing appeared
   * until the bytes, the sha256, the GCS write and the DB insert had all finished, so a large drop
   * showed no feedback for seconds. Because the job's identity is client-minted, the row the user
   * sees at drop time is the same object that becomes an indexing job — surfaces observe
   * `pendingUploadsIn()` and hold no upload state of their own. Ten call sites used to each mint,
   * track and reap their own optimistic rows; seven had hand-written start/finish pairs, one leaked
   * them permanently and one showed nothing at all.
   *
   * **One request per file, not one per drop.** A drop is still a single gesture — the fan-out is
   * transport only. Batching made every file share one fate: the endpoint refuses the WHOLE batch
   * when any one file is oversize, a stalled request wedged all of them, and a big file held back
   * the rows for the small ones dropped with it. Per file, each row succeeds, fails and cancels on
   * its own, and the response maps to exactly one job instead of by array position.
   */
  async function upload(
    files: File[],
    opts: {
      path?: string
      source?: IndexSource
      title?: string
      /** OneDrive re-upload reference (AlexAmin/marcus#845 — `OneDrivePickerModal.vue` routes
       *  through this same entry point rather than posting the old buffered endpoint directly).
       *  Honoured only for a single-file call: a batch import calls `upload()` once per file. */
      onedrive?: OneDriveRef
    } = {},
  ): Promise<UploadedItem[]> {
    const path = opts.path ?? '/'
    const source = opts.source ?? 'library'

    const started = files.map((file) => {
      const job = pushJob(mintJob({ name: file.name, path, source }))
      return { file, job }
    })

    // Opened on the DROP, not on the response: the panel is where the work went, and showing it a
    // second later would be the same beat-of-nothing this feature exists to remove. Starting work
    // reveals; the feed discovering work does not (ACTIVITY_CENTER §4d).
    if (started.length) reveal()

    // Bounded because the split moved concurrency control to the client: the endpoint's own
    // `pLimit(8)` governed files WITHIN one request and no longer sees a batch at all. Ten files
    // dropped at once must not become ten simultaneous connections.
    const limit = pLimit(UPLOAD_CONCURRENCY)
    const settled = await Promise.all(started.map(({ file, job }) => limit(async () => {
      // `titleOverride` is honoured by the endpoint only for a single-file request. Every request is
      // single-file now, so sending it unconditionally would silently rename every file in a drop.
      const title = files.length === 1 ? opts.title : undefined
      const onedrive = files.length === 1 ? opts.onedrive : undefined
      return uploadOne(file, job, path, title, onedrive)
    })))

    trim()
    ensurePoll()

    const created = settled.filter((item): item is UploadedItem => item !== null)
    // Callers that attach what they uploaded (the chat chips) need to know the whole drop failed.
    // Individual failures are already on screen as their own rows, so they do NOT throw.
    if (!created.length && files.length) throw uploadError(started[0]!.job)
    return created
  }

  /**
   * One file, one request, one abort handle. The wire itself is `sendUpload`
   * (`composables/documents-indexing/uploadTransport.ts`); this binds its answer to the job.
   *
   * Returns `null` rather than throwing on failure: a per-file failure is already visible as its own
   * row, and letting it reject would take the rest of the drop down with it — the exact coupling
   * the per-file split exists to remove.
   */
  async function uploadOne(
    file: File,
    job: IndexJob,
    path: string,
    title?: string,
    onedrive?: OneDriveRef,
  ): Promise<UploadedItem | null> {
    // Fail before the bytes go anywhere. The server enforces the same limit — this only spares the
    // user a minutes-long upload (hash + POST) that was always going to be refused.
    if (isTooLarge(file.size)) {
      fail(job, t('indexing.uploadTooLarge', { name: file.name, limit: MAX_UPLOAD_LABEL }), 'too_large')
      return null
    }

    const controller = new AbortController()
    aborters.set(job.id, controller)
    try {
      const item = await sendUpload(file, { apiFetch, path, title, onedrive, signal: controller.signal })
      if (!item) {
        fail(job, t('indexing.uploadFailedGeneric'), 'unknown')
        return null
      }
      claim(job, item)
      return item
    } catch (err: any) {
      // Cancelling already settled the row with the user's own wording; an AbortError arriving after
      // that must not overwrite it with a failure they didn't cause.
      if (controller.signal.aborted) return null
      fail(job, uploadFailureMessage(err), requestFailureKind(err))
      return null
    } finally {
      aborters.delete(job.id)
    }
  }

  /**
   * A refused upload becomes a `failed` row, never a removed one.
   *
   * Before this, an upload that threw produced a toast and nothing else — the work vanished from the
   * Activity Center as if it had never been attempted, which is the same silence MAR-113 exists to
   * end. It happens to be the failure mode users notice most.
   */
  function uploadFailureMessage(err: any): string {
    const code = err?.data?.data?.code
    if (code === 'STORAGE_QUOTA_EXCEEDED') return t('indexing.uploadQuota')
    if (code === 'FILE_TOO_LARGE') {
      return t('indexing.uploadTooLarge', { name: err?.data?.data?.fileName ?? '', limit: MAX_UPLOAD_LABEL })
    }
    return apiErrorMessage(err, t('indexing.uploadFailedGeneric'))
  }

  function uploadError(job: IndexJob): Error {
    return new Error(job.errorMessage || t('indexing.uploadFailedGeneric'))
  }

  /**
   * Stop waiting on an upload — the user's own cancel, and the only way out of a request that hangs
   * without ever resolving or erroring (a sleeping laptop, a dropped connection the browser has not
   * given up on yet).
   *
   * It means "stop waiting", NOT "undo". Bytes already written server-side stay written, and that is
   * fine: the feed's discovery half reports whatever actually landed, so the client stops guessing
   * and the server tells us the truth.
   */
  function cancel(jobId: string) {
    const job = jobs.value.find(j => j.id === jobId)
    if (!job || job.state !== 'uploading') return
    fail(job, t('indexing.uploadCancelled'), 'cancelled')
    aborters.get(jobId)?.abort()
    aborters.delete(jobId)
  }

  return { upload, cancel }
}
